import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogoIcon } from "../../components/icons";
import { prereleaseAPI } from "../../lib/api";
import styles from "./LandingPage.module.css";

const features = [
  {
    icon: "\u{1F399}\u{FE0F}",
    title: "Podcast Hosting",
    desc: "Upload, manage, and distribute your podcasts from one powerful dashboard.",
  },
  {
    icon: "\u{1F4CA}",
    title: "Analytics",
    desc: "Detailed listener analytics so you know exactly how your show is performing.",
  },
  {
    icon: "\u{1F310}",
    title: "Networks",
    desc: "Build podcast networks with shared revenue splits and cross-promotion tools.",
  },
  {
    icon: "\u{1F4B3}",
    title: "Monetization",
    desc: "Built-in billing and Stripe Connect integration for listener subscriptions.",
  },
  {
    icon: "\u{1F465}",
    title: "Teams",
    desc: "Collaborate with role-based access, teams, and granular podcast-level permissions.",
  },
  {
    icon: "\u{26A1}",
    title: "Built for Speed",
    desc: "React, Vite, and Express on Bun deliver fast builds, instant HMR, and snappy API responses.",
  },
];

export default function LandingPage() {
  const [prereleaseMode, setPrereleaseMode] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    prereleaseAPI
      .getConfig()
      .then((res) => setPrereleaseMode(res.prereleaseMode))
      .catch(() => {});
  }, []);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await prereleaseAPI.joinWaitlist(email);
      setSubmitted(true);
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setError(message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.landing}>
      <nav className={styles.nav}>
        <Link to="/" className={styles.logoLink}>
          <div className={styles.logoMark}>
            <LogoIcon size={22} />
          </div>
          <span className={styles.logoText}>Poddify</span>
        </Link>

        {!prereleaseMode && (
          <div className={styles.navLinks}>
            <Link to="/login" className={styles.navLink}>
              Sign in
            </Link>
            <Link to="/register" className={styles.navCta}>
              Get started
            </Link>
          </div>
        )}
      </nav>

      <section className={styles.hero}>
        {prereleaseMode ? (
          <>
            <span className={styles.badge}>Coming Soon</span>
            <h1 className={styles.heroTitle}>
              Podcast hosting <span className={styles.heroTitleAccent}>done right</span>
            </h1>
            <p className={styles.heroSub}>
              Poddify is a modern podcast hosting platform with built-in analytics, monetization,
              and team collaboration. We&apos;re putting the finishing touches on our MVP.
            </p>
            {submitted ? (
              <div className={styles.waitlistSuccess}>
                <span className={styles.waitlistSuccessIcon}>✓</span>
                You&apos;re on the list! We&apos;ll let you know when we launch.
              </div>
            ) : (
              <form className={styles.waitlistForm} onSubmit={handleWaitlistSubmit}>
                <input
                  type="email"
                  className={styles.waitlistInput}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button type="submit" className={styles.ctaPrimary} disabled={submitting}>
                  {submitting ? "Joining..." : "Get notified"}
                </button>
                {error && <p className={styles.waitlistError}>{error}</p>}
              </form>
            )}
          </>
        ) : (
          <>
            <span className={styles.badge}>Now in Beta</span>
            <h1 className={styles.heroTitle}>
              Podcast hosting <span className={styles.heroTitleAccent}>done right</span>
            </h1>
            <p className={styles.heroSub}>
              Upload, distribute, and monetize your podcasts with built-in analytics, team
              collaboration, and network tools.
            </p>
            <div className={styles.heroCtas}>
              <Link to="/register" className={styles.ctaPrimary}>
                Create free account
              </Link>
              <Link to="/login" className={styles.ctaSecondary}>
                Sign in
              </Link>
            </div>
          </>
        )}
      </section>

      <section className={styles.features}>
        <div className={styles.featuresHeading}>
          <p className={styles.featuresTag}>Everything you need</p>
          <h2 className={styles.featuresTitle}>Built for podcasters</h2>
        </div>

        <div className={styles.featureGrid}>
          {features.map((f) => (
            <div key={f.title} className={styles.featureCard}>
              <div className={styles.featureIcon}>{f.icon}</div>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className={styles.footer}>
        Poddify &middot; Built with React, Express &amp; Bun
      </footer>
    </div>
  );
}
